import { useNavigate } from 'react-router-dom'
import { useLLRDashboard, useRadars } from '../hooks/useLLR'
import { PageHeader } from '@/shared/components/ui/PageHeader'
import { KpiCard } from '@/shared/components/ui/KpiCard'
import { EmptyState } from '@/shared/components/ui/EmptyState'
import { Button } from '@/shared/components/ui/Button'
import { MapPin, TrendingUp, Plus, Bell, ChevronRight, Radar } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

/* eslint-disable @typescript-eslint/no-explicit-any */

const intensityColor = (score: number) => {
  if (score >= 80) return 'text-semantic-danger bg-brick-light border-brick/20'
  if (score >= 65) return 'text-semantic-warning bg-gold-light border-gold/20'
  if (score >= 50) return 'text-gold-dark bg-gold-light border-gold/20'
  return 'text-ink-muted bg-paper-warm border-ink-border'
}

function DetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-ink-border/20 last:border-0">
      <span className="text-[12px] text-ink-muted">{label}</span>
      <span className="text-[13px] font-semibold text-ink">{value}</span>
    </div>
  )
}

export function RadarHubPage() {
  const navigate = useNavigate()
  const { data: dash, isLoading: dashLoading } = useLLRDashboard()
  const { data: radars, isLoading: radarsLoading } = useRadars()

  return (
    <div className="space-y-6">
      <PageHeader
        moduleId="llr"
        moduleFullName="Local Lead Radar"
        title="Radar Hub"
        subtitle="Détection prédictive de la demande locale"
        actions={<Button onClick={() => navigate('/llr/radars/new')}><Plus size={16} /> Nouveau Radar</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard label="Radars actifs" value={dash?.active_radars ?? 0} accentColor="#C9A84C" loading={dashLoading}
          detailTitle="Radars actifs"
          detailNode={
            <div className="space-y-3">
              {radars?.length ? radars.map((r: any) => (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg bg-paper-warm/50">
                  <Radar size={14} className="text-gold flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-ink truncate">{r.name}</p>
                    <p className="text-[11px] text-ink-faint">{r.geo_label || 'Zone non définie'} · {(r.keywords as any[])?.length ?? 0} mots-clés</p>
                  </div>
                  <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium',
                    r.status === 'active' ? 'bg-teal-light text-teal-dark' : 'bg-paper-warm text-ink-faint')}>{r.status}</span>
                </div>
              )) : <p className="text-[12px] text-ink-faint text-center py-4">Aucun radar configuré</p>}
            </div>
          } />
        <KpiCard label="Mots-clés surveillés" value={dash?.total_keywords ?? 0} accentColor="#C9A84C" loading={dashLoading}
          detailTitle="Mots-clés par radar"
          detailNode={
            <div className="space-y-1">
              {radars?.length ? radars.map((r: any) => (
                <DetailRow key={r.id} label={r.name} value={`${(r.keywords as any[])?.length ?? 0} mots-clés`} />
              )) : <p className="text-[12px] text-ink-faint text-center py-4">Aucun radar</p>}
              <div className="pt-2 mt-2 border-t border-ink-border/40">
                <DetailRow label="Total" value={dash?.total_keywords ?? 0} />
              </div>
            </div>
          } />
        <KpiCard label="Alertes non lues" value={dash?.unread_alerts ?? 0} accentColor="#E8562A" loading={dashLoading}
          detailTitle="Alertes non lues"
          detailNode={
            <div className="space-y-2">
              {dash?.recent_alerts?.length ? dash.recent_alerts.filter((a: any) => a.status === 'unread').slice(0, 8).map((a: any) => (
                <div key={a.id} className={cn('p-3 rounded-lg border-l-[3px]',
                  (a.intensity_score as number) >= 80 ? 'border-l-brick bg-brick-light/20' :
                  (a.intensity_score as number) >= 65 ? 'border-l-gold bg-gold-light/20' : 'border-l-ink-border bg-paper-warm/30')}>
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] font-medium text-ink">{a.keyword}</span>
                    <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full',
                      (a.intensity_score as number) >= 80 ? 'bg-brick text-white' : (a.intensity_score as number) >= 65 ? 'bg-gold text-onyx' : 'bg-ink-muted text-white')}>
                      {(a.intensity_score as number).toFixed(0)}
                    </span>
                  </div>
                </div>
              )) : <p className="text-[12px] text-ink-faint text-center py-4">Aucune alerte non lue</p>}
            </div>
          } />
        <KpiCard label="Intensité moyenne" value={dash?.avg_intensity ? `${dash.avg_intensity}/100` : '—'} accentColor="#C9A84C" loading={dashLoading}
          detailTitle="Intensité par radar"
          detailNode={
            <div className="space-y-1">
              {radars?.length ? radars.map((r: any) => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-ink-border/20 last:border-0">
                  <span className="text-[12px] text-ink-muted">{r.name}</span>
                  <div className="flex items-center gap-2">
                    {r.latest_score > 0 && (
                      <div className="w-20 h-1.5 bg-paper-warm rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${r.latest_score}%`, background: r.latest_score >= 80 ? '#E8562A' : r.latest_score >= 50 ? '#C9A84C' : '#9A917F' }} />
                      </div>
                    )}
                    <span className="text-[13px] font-semibold text-ink w-10 text-right">{r.latest_score ? (r.latest_score as number).toFixed(0) : '—'}</span>
                  </div>
                </div>
              )) : <p className="text-[12px] text-ink-faint text-center py-4">Aucun radar</p>}
            </div>
          } />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Radars List */}
        <div className="lg:col-span-2 bg-white rounded-card border border-ink-border shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-ink-border/30">
            <h2 className="font-serif text-sm font-semibold text-ink">Mes Radars</h2>
            <span className="text-xs text-ink-faint">{radars?.length ?? 0} radar(s)</span>
          </div>
          <div className="divide-y divide-ink-border/30">
            {radarsLoading ? (
              <div className="p-8 text-center text-ink-faint">Chargement…</div>
            ) : !radars?.length ? (
              <EmptyState
                compact
                title="Aucun radar configuré"
                description="Créez votre premier radar pour surveiller la demande locale."
                action={{ label: '+ Nouveau radar', onClick: () => navigate('/llr/radars/new'), variant: 'primary' }}
              />
            ) : (
              radars.map((r: Record<string, unknown>, idx: number) => (
                <button key={r.id as string}
                  onClick={() => navigate(`/llr/alerts?radar_id=${r.id}`)}
                  className={cn('w-full flex items-center gap-4 px-5 py-4 hover:bg-gold-light/30 transition-colors text-left', idx % 2 === 1 && 'bg-paper')}>
                  <div className="w-10 h-10 rounded-lg bg-gold-light flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-gold-dark" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[13px] font-medium text-ink truncate">{r.name as string}</p>
                      <span className={cn('text-[10px] px-2.5 py-0.5 rounded-full font-medium',
                        r.status === 'active' ? 'bg-teal-light text-teal-dark' :
                        r.status === 'paused' ? 'bg-gold-light text-gold-dark' : 'bg-paper-warm text-ink-faint')}>
                        {r.status as string}
                      </span>
                    </div>
                    <p className="text-[11px] font-light text-ink-faint mt-0.5">
                      {(r.geo_label as string) || `${r.geo_lat}, ${r.geo_lng}`} · {(r.keywords as string[])?.length ?? 0} mot(s)-clé(s) · {r.alert_count as number} alerte(s)
                    </p>
                  </div>
                  {(r.latest_score as number) && (
                    <div className={cn('text-[10px] font-bold px-2.5 py-1 rounded-full border', intensityColor(r.latest_score as number))}>
                      {(r.latest_score as number).toFixed(0)}
                    </div>
                  )}
                  <ChevronRight size={14} className="text-ink-faint flex-shrink-0" />
                </button>
              ))
            )}
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="bg-white rounded-card border border-ink-border shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-ink-border/30">
            <h2 className="font-serif text-sm font-semibold text-ink">Alertes récentes</h2>
            <button onClick={() => navigate('/llr/alerts')} className="text-[11px] text-brick font-medium hover:text-brick-dark">Tout voir</button>
          </div>
          <div className="divide-y divide-ink-border/30">
            {dashLoading ? (
              <div className="p-6 text-center text-ink-faint">Chargement…</div>
            ) : !dash?.recent_alerts?.length ? (
              <div className="p-6 text-center">
                <div className="w-10 h-10 rounded-lg bg-gold-light flex items-center justify-center mx-auto mb-3">
                  <Bell size={18} className="text-gold/40" />
                </div>
                <p className="font-serif text-[13px] text-ink">Aucune alerte</p>
                <p className="text-[11px] font-light text-ink-faint mt-1">Les alertes apparaissent quand vos mots-clés dépassent un seuil.</p>
              </div>
            ) : (
              dash.recent_alerts.map((a: Record<string, unknown>) => (
                <div key={a.id as string}
                  className={cn('px-5 py-3 border-l-[3px]',
                    (a.intensity_score as number) >= 80 ? 'border-l-brick bg-brick-light/30' :
                    (a.intensity_score as number) >= 65 ? 'border-l-gold bg-gold-light/30' :
                    'border-l-ink-border bg-paper-warm/30')}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] font-medium text-ink truncate">{a.keyword as string}</span>
                    <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full',
                      (a.intensity_score as number) >= 80 ? 'bg-brick text-white' :
                      (a.intensity_score as number) >= 65 ? 'bg-gold text-onyx' :
                      'bg-ink-muted text-white')}>
                      {(a.intensity_score as number).toFixed(0)}
                    </span>
                  </div>
                  {(a.variation_pct as number) && (
                    <div className="flex items-center gap-1 text-[11px] text-ink-muted">
                      <TrendingUp size={11} /> +{(a.variation_pct as number).toFixed(0)}%
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
